import type { TBotConfig, TBotConfigExtended } from '~entities/bot';
import { gridApi } from '~services/api/grid';
import { api, apiTags } from '~shared/api/origin';

import { getCreditsApi } from '../credits/dynamic';
import { userApi } from '../user';
import type {
	BuyBotGalleryMediaApiResponse,
	GetBotAccessInfoApiResponse,
	GetBotGalleryApiResponse,
	GetBotModelsApiResponse,
	GetBotProfileStatsApiResponse,
	GetBotsAvailabilityApiResponse,
	GetContactsApiResponse,
	GetContactsApiResponseTransformed,
} from './types';
import { transformChatsMessage } from './utils';

export const {
	endpoints,
	util,
	useGetContactsQuery,
	useGetBotsAvailabilityQuery,
	useReportAndDeleteBotMutation,
	useChangeBotAccessMutation,
	useGetBotAccessInfoQuery,
	useGetBotProfileQuery,
	useLazyGetBotProfileQuery,
	useGetBotModelsQuery,
	useSetBotModelMutation,
	useGetBotStatsQuery,
	useGetBotGalleryQuery,
	useBuyMediaMutation,
} = api.injectEndpoints({
	endpoints: (build) => ({
		getContacts: build.query<
			GetContactsApiResponseTransformed,
			{ userId: string }
		>({
			async queryFn({ userId }, _queryApi, _extraOptions, fetchWithBQ) {
				const [botsResponse, availabilityResponse] = await Promise.all([
					fetchWithBQ(`/ai/configuration/${userId}/bots`),
					fetchWithBQ(`/ai/chats/userId/${userId}/v2`),
				]);
				const error = botsResponse.error || availabilityResponse.error;
				if (error) {
					return { error };
				}

				const bots = botsResponse.data as GetContactsApiResponse;
				const availability = (
					availabilityResponse.data as GetBotsAvailabilityApiResponse
				).chats;

				const botsTransformed: GetContactsApiResponseTransformed = {
					contacts: {},
					inaccessibleContacts: {},
					nsfwCount: (
						availabilityResponse.data as GetBotsAvailabilityApiResponse
					).nsfwCount,
				};
				for (const key of Object.keys(bots)) {
					const botId = bots[key].clientId;
					const botAvailableInfo = availability.find(({ uid }) => key === uid);
					if (botAvailableInfo) {
						botsTransformed.contacts[botId] = {
							...bots[key],
							lastMessage: botAvailableInfo.lastMessage
								? transformChatsMessage(botAvailableInfo.lastMessage)
								: null,
							unreadMessagesCount: botAvailableInfo.counters.unreadCount,
							totalMessagesCount: botAvailableInfo.counters.totalCount,
							preConditions: botAvailableInfo.conditions?.preCondition,
							postConditions: botAvailableInfo.conditions?.postCondition,
							statistics: botAvailableInfo.statistics,
							generations: [],
							isTyping: false,
						};
					} else {
						botsTransformed.inaccessibleContacts[botId] = {
							...bots[key],
						};
					}
				}

				return {
					data: botsTransformed,
				};
			},
			async onQueryStarted(_, { dispatch }) {
				const creditsApi = await getCreditsApi();
				dispatch(creditsApi.util.invalidateTags([apiTags.shop]));
			},
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs.userId}`;
			},
			providesTags: [apiTags.contacts],
		}),

		getBotsAvailability: build.query<GetBotsAvailabilityApiResponse, string>({
			async queryFn(userId, _queryApi, _extraOptions, fetchWithBQ) {
				const response = await fetchWithBQ(`/ai/chats/userId/${userId}/v2`);
				return response.error
					? {
							error: response.error,
					  }
					: {
							data: response.data as GetBotsAvailabilityApiResponse,
					  };
			},
			async onQueryStarted(userId, { dispatch, queryFulfilled }) {
				try {
					const { data: res } = await queryFulfilled;
					dispatch(
						util.updateQueryData('getContacts', { userId }, (draft) => {
							for (const key of Object.keys(draft.contacts)) {
								const botAvailableInfo = res.chats.find(
									({ botId }) => key === botId
								);
								if (botAvailableInfo) {
									draft.contacts[key].preConditions =
										botAvailableInfo.conditions?.preCondition;
									draft.contacts[key].postConditions =
										botAvailableInfo.conditions?.postCondition;
								}
							}
						})
					);
				} catch {}
			},
		}),
		reportAndDeleteBot: build.mutation<
			{},
			{
				userId: string;
				recipientId: string;
				reason: string;
				reportText: string;
			}
		>({
			query: ({ userId, reportText, recipientId, reason }) => ({
				url: `/ai/bots/${userId}/${recipientId}/reports`,
				method: 'POST',
				body: {
					reason,
					comment: reportText,
				},
			}),
			async onQueryStarted(
				{ userId, recipientId },
				{ dispatch, queryFulfilled, getState }
			) {
				try {
					await queryFulfilled;
					dispatch(
						util.updateQueryData('getContacts', { userId }, (draft) => {
							delete draft.contacts[recipientId];
						})
					);

					for (const { originalArgs } of gridApi.util.selectInvalidatedBy(
						getState(),
						[apiTags.grid]
					)) {
						dispatch(
							gridApi.util.updateQueryData('getGrid', originalArgs, (draft) => {
								draft.botGrid = draft.botGrid.filter(
									({ clientId }) => clientId !== +recipientId
								);
							})
						);
					}
				} catch {}
			},
		}),
		changeBotAccess: build.mutation<
			{},
			{ userId: string; recipientId: string; access: 'public' | 'private' }
		>({
			query: ({ userId, recipientId, access }) => ({
				url: `/ai/bots/${userId}/${recipientId}/profile`,
				method: 'PATCH',
				body: {
					access,
				},
			}),
			onQueryStarted({ userId, recipientId, access }, { dispatch }) {
				dispatch(
					util.updateQueryData('getContacts', { userId }, (draft) => {
						draft.contacts[recipientId].access = access;
					})
				);
			},
			invalidatesTags: [apiTags.grid],
		}),
		getBotAccessInfo: build.query<
			GetBotAccessInfoApiResponse,
			{ userId: string; recipientId: string }
		>({
			query: ({ userId, recipientId }) => ({
				url: `/ai/bots/${userId}/${recipientId}/access`,
			}),
		}),
		getBotProfile: build.query<
			TBotConfig | null,
			{ userId: string; recipientId: string | number }
		>({
			async queryFn(
				{ userId, recipientId },
				_queryApi,
				_extraOptions,
				fetchWithBQ
			) {
				const response = await fetchWithBQ(
					`/ai/bots/${userId}/${recipientId}/profile`
				);

				if (!response.error) {
					return { data: response.data as TBotConfig };
				} else if (
					response.error.status === 403 ||
					response.error.status === 404
				) {
					return { data: null };
				}
				return {
					error: response.error,
				};
			},
			async onQueryStarted(
				{ userId, recipientId },
				{ dispatch, queryFulfilled }
			) {
				try {
					const { data } = await queryFulfilled;
					if (!data) return;
					dispatch(
						util.updateQueryData('getContacts', { userId }, (draft) => {
							if (!draft.contacts[recipientId]) {
								draft.contacts[recipientId] = {
									...data,
									lastMessage: null,
									unreadMessagesCount: 0,
									totalMessagesCount: 0,
									preConditions: undefined,
									postConditions: undefined,
									statistics: undefined,
									generations: [],
									isTyping: false,
								} as TBotConfigExtended;
							}
						})
					);
				} catch {}
			},
		}),
		getBotModels: build.query<GetBotModelsApiResponse, { userId: string }>({
			query: ({ userId }) => ({ url: `ai/models/${userId}` }),
		}),
		setBotModel: build.mutation<{}, { userId: string; code: string }>({
			query: ({ userId, code }) => ({
				url: `ai/models/${userId}/active`,
				method: 'PUT',
				body: { code },
			}),
			async onQueryStarted({ userId, code }, { dispatch, queryFulfilled }) {
				await queryFulfilled;
				dispatch(
					userApi.util.updateQueryData('userConfig', { userId }, (draft) => {
						draft.activeModel = code;
					})
				);
			},
			invalidatesTags: [apiTags.userConfig],
		}),
		getBotStats: build.query<
			GetBotProfileStatsApiResponse,
			{ userId: string; recipientId: string }
		>({
			query: ({ userId, recipientId }) => ({
				url: `/ai/bots/${userId}/${recipientId}/stats/v2`,
			}),
		}),
		getBotGallery: build.query<
			GetBotGalleryApiResponse,
			{
				userId: string;
				recipientId: string;
				page: number;
				pageSize?: number;
			}
		>({
			query: ({ userId, recipientId, page, pageSize = 28 }) => ({
				url: `/ai/${userId}/bot/gallery/${recipientId}`,
				params: {
					page,
					pageSize,
				},
			}),
			transformResponse: (
				response: GetBotGalleryApiResponse | null,
				_,
				{ page }
			) => {
				if (response === null) {
					return {
						currentPage: 0,
						receivedCount: 0,
						totalLength: 0,
						media: [],
					};
				}

				const { media } = response;

				return {
					media,
					currentPage: page,
					receivedCount: media.length,
					totalLength: media.length,
				};
			},
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs?.userId}-${queryArgs?.recipientId}`;
			},
			forceRefetch: ({ currentArg, endpointState }) => {
				return (
					currentArg?.page === 0 ||
					(currentArg?.page ?? 0) >
						((endpointState?.data as GetBotGalleryApiResponse | undefined)
							?.currentPage ?? 0)
				);
			},
			merge: (currentState, incomingState) => {
				if (incomingState.currentPage === 0) {
					Object.assign(currentState, incomingState);
					return;
				}

				currentState.currentPage = incomingState.currentPage;
				currentState.receivedCount = incomingState.receivedCount;
				currentState.totalLength =
					currentState.totalLength + incomingState.receivedCount;

				incomingState.media.forEach((item) => {
					const existedIndex = currentState.media.findIndex(
						({ mediaId, dialogMediaId }) =>
							(item.dialogMediaId !== undefined &&
								dialogMediaId === item.dialogMediaId) ||
							(item.mediaId !== undefined && mediaId === item.mediaId)
					);

					if (existedIndex > -1) {
						currentState.media[existedIndex] = item;
						currentState.totalLength -= 1;
					} else {
						currentState.media.push(item);
					}
				});
			},
			providesTags: [apiTags.botGallery],
		}),
		buyMedia: build.mutation<
			BuyBotGalleryMediaApiResponse,
			{
				userId: string;
				dialogMediaId: string;
				recipientId: string;
			}
		>({
			query: ({ userId, dialogMediaId }) => ({
				url: `/ai/${userId}/dialogs/media/buy`,
				method: 'PUT',
				body: { dialogMediaId },
			}),
			async onQueryStarted(
				{ userId, dialogMediaId, recipientId },
				{ dispatch, queryFulfilled }
			) {
				const { data } = await queryFulfilled;
				const newMedia = data.media;

				dispatch(
					util.updateQueryData(
						'getBotGallery',
						{ userId, recipientId, page: 0 },
						(draft) => {
							const mediaItem = draft.media.find(
								(item) => item.dialogMediaId === dialogMediaId
							);
							if (mediaItem) {
								Object.assign(mediaItem, newMedia);

								const { tariffication } = mediaItem;

								if (tariffication) {
									tariffication.access = true;
									tariffication.paid = true;
								}
							}
						}
					)
				);
			},
		}),
	}),
});

export const {
	getBotProfile: { useQueryState: useGetBotProfileQueryState },
} = endpoints;
